import React, { ReactNode, useRef, useState } from 'react'
import styled from 'styled-components'

import {
    BaseButton,
    Text,
    useSubscribeToClickOutsideEvent,
    ZIndex,
} from '@tuhi/components'
import { selectAuthState } from 'app/store/selectors'
import { useSelector } from 'react-redux'

import ChevronDownSvg from '@tuhi/components/assets/icons/header/chevronDown.svg'
import ChevronUpSvg from '@tuhi/components/assets/icons/header/chevronUp.svg'
import SignOutIcon from '@tuhi/components/assets/icons/navigationMenu/signOut.svg'
import PersonSvg from '@tuhi/components/assets/icons/navigationMenu/person.svg'
import { PositiveMenuItem } from 'app/features/sideMenu/positiveMenuItem'
import { RouteUrl } from 'app/routes'
import { NegativeMenuItem } from 'app/features/sideMenu/negativeMenuItem'
import { useSignOut } from 'app/hooks/useSignOut'
import { Link } from 'react-router-dom'
import {
    isFreeSubscription,
    getConsultsLeft,
} from 'app/models/domain/user/tuhiUser'
import { media } from '@tuhi/components/src/theme/breakpoints'
import { useMobileMenu } from 'app/hooks/useMobileMenu'

export const HEADER_SHADOW = '0 4px 4px 0 rgba(0, 0, 0, 0.05)'

const HeaderContainerWrapper = styled.div<{ $hasShadow: boolean }>`
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
    height: 68px;
    padding: 8px 40px;
    padding-bottom: 8px;
    background-color: ${(p) => p.theme.colors.white};
    box-shadow: ${(p) => (p.$hasShadow ? HEADER_SHADOW : 'none')};

    ${media.tablet} {
        padding: 8px 24px;
    }

    ${media.mobile} {
        padding: 8px 16px;
    }
`

const LeftContainer = styled.div<{ $hasGap: boolean }>`
    display: flex;
    align-items: center;
    gap: ${(p) => (p.$hasGap ? '16px' : '0')};
    min-width: 0;
`

const RightContainer = styled.div`
    display: flex;
    flex: 1;
    justify-content: flex-end;
    gap: 32px;

    ${media.tablet} {
        gap: 16px;
    }

    ${media.mobile} {
        gap: 12px;
    }
`

const TrialContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    background: linear-gradient(85deg, #b3d9c8 -20.89%, #f5faf9 103.56%);
    padding: 8px 24px 8px 8px;
    border-radius: 8px;
    gap: 16px;
`

const ConsultLeftContainer = styled.span`
    display: flex;

    align-items: center;
    gap: 8px;
`

const ConsultCountContainer = styled.div`
    padding: 2px 4px;
    border-radius: 4px;
    background: linear-gradient(228deg, #56df4a -98.72%, #0a8447 127.58%);
`

const ViewPlansWrapper = styled(Link)`
    cursor: pointer;
    text-decoration: none;
`

const ProfileContainer = styled(BaseButton)`
    display: flex;
    align-items: center;
    gap: 16px;
`

const ProfileIcon = styled.div`
    width: 52px;
    height: 52px;
    border: ${(p) => `2px solid ${p.theme.colors.tertiary.tertiary200}`};
    border-radius: 50%;
`

const ProfileMenuContainer = styled.div`
    z-index: ${ZIndex.Dropdown};
    position: absolute;
    display: flex;
    flex-direction: column;
    width: 224px;
    padding: 8px;
    gap: 8px;

    background: ${(p) => p.theme.colors.white};
    box-shadow: 0px 1px 10px 0px rgba(0, 0, 0, 0.3);
    border-radius: 8px;

    top: 78px;
    right: 16px;
`

const HamburgerButton = styled(BaseButton)`
    display: none;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 4px;
    width: 44px;
    height: 44px;
    flex-shrink: 0;

    ${media.mobile} {
        display: flex;
    }
`

const HamburgerLine = styled.span`
    display: block;
    width: 20px;
    height: 2px;
    background-color: ${(p) => p.theme.colors.primary.default};
    border-radius: 1px;
`

const PageTitle = styled(Text)`
    ${media.mobile} {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 140px;
    }
`

const ConsultsLeftText = styled(Text)`
    ${media.tablet} {
        display: none;
    }

    ${media.mobile} {
        display: none;
    }
`

const ProfileNameText = styled(Text)`
    ${media.mobile} {
        display: none;
    }
`

const ChevronWrapper = styled.span`
    display: flex;
    align-items: center;

    ${media.mobile} {
        display: none;
    }
`

interface Props {
    title?: string
    children?: ReactNode
    hasGap?: boolean
    /**
     * Draw the drop shadow under the header. Pages that stack more content
     * under the header in one pinned block (the consult view) turn it off and
     * draw the shadow under that block instead.
     */
    hasShadow?: boolean
}

export const AuthenticatedHeader: React.FC<Props> = ({
    title,
    children,
    hasGap = true,
    hasShadow = true,
}) => {
    const { currentUser } = useSelector(selectAuthState)
    const signOut = useSignOut()
    const ref = useRef(null)
    const { toggle } = useMobileMenu()

    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false)

    const profileMenuItem = {
        icon: <PersonSvg />,
        route: RouteUrl.Profile,
    }

    useSubscribeToClickOutsideEvent(ref, () => {
        setIsProfileMenuOpen(false)
    })

    return (
        <HeaderContainerWrapper
            $hasShadow={hasShadow}
            data-testid="authenticated-header"
        >
            <LeftContainer $hasGap={hasGap}>
                <HamburgerButton onClick={toggle} aria-label="Open menu">
                    <HamburgerLine />
                    <HamburgerLine />
                    <HamburgerLine />
                </HamburgerButton>
                <PageTitle size="xxLarge" $weight="bold" color="brand">
                    {title}
                </PageTitle>
                {children}
            </LeftContainer>

            <RightContainer>
                {currentUser && isFreeSubscription(currentUser) && (
                    <TrialContainer>
                        <ConsultLeftContainer>
                            <ConsultCountContainer>
                                <Text
                                    size="xLarge"
                                    $weight="regular"
                                    color="white"
                                >
                                    {getConsultsLeft(currentUser)}
                                </Text>
                            </ConsultCountContainer>
                            <ConsultsLeftText
                                size="xSmall"
                                $weight="medium"
                                color="brand"
                            >
                                Consults left this month
                            </ConsultsLeftText>
                        </ConsultLeftContainer>

                        <ViewPlansWrapper to={RouteUrl.PlanPricing.path}>
                            <Text size="xSmall" $weight="bold" color="brand">
                                View plans
                            </Text>
                        </ViewPlansWrapper>
                    </TrialContainer>
                )}

                <ProfileContainer
                    onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                >
                    <ProfileIcon />
                    <ProfileNameText
                        size="xSmall"
                        $weight="medium"
                        color="brand"
                        $underline={isProfileMenuOpen}
                    >
                        {currentUser?.firstName}
                    </ProfileNameText>

                    <ChevronWrapper>
                        {isProfileMenuOpen ? (
                            <ChevronUpSvg />
                        ) : (
                            <ChevronDownSvg />
                        )}
                    </ChevronWrapper>
                </ProfileContainer>
            </RightContainer>

            {isProfileMenuOpen && (
                <ProfileMenuContainer ref={ref}>
                    <PositiveMenuItem
                        key={profileMenuItem.route.name}
                        menu={profileMenuItem}
                        isSelected={false}
                        isCollapsed={false}
                    />

                    <NegativeMenuItem
                        key="Sign out"
                        icon={<SignOutIcon />}
                        text="Sign out"
                        onClick={signOut}
                        isCollapsed={false}
                    />
                </ProfileMenuContainer>
            )}
        </HeaderContainerWrapper>
    )
}
